export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export type InstallPlatform =
  | 'ios'
  | 'android'
  | 'macos'
  | 'windows'
  | 'linux'
  | 'other';

export interface InstallState {
  installed: boolean;
  nativePromptAvailable: boolean;
  platform: InstallPlatform;
  iosSafari: boolean;
  macSafari: boolean;
}

const SNOOZE_KEY = 'coconnect_pwa_install_snooze_until';
const INSTALLED_KEY = 'coconnect_pwa_installed';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(state: InstallState) => void>();

function isStandalone(): boolean {
  const navigatorWithStandalone =
    navigator as Navigator & { standalone?: boolean };

  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    navigatorWithStandalone.standalone === true
  );
}

function platform(): InstallPlatform {
  const ua = navigator.userAgent;

  if (/iPad|iPhone|iPod/i.test(ua)) return 'ios';

  // iPadOS can expose itself as Macintosh while supporting touch.
  if (
    /Macintosh/i.test(ua) &&
    navigator.maxTouchPoints > 1
  ) {
    return 'ios';
  }

  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
  if (/Linux/i.test(ua)) return 'linux';

  return 'other';
}

function safari(): boolean {
  const ua = navigator.userAgent;

  return (
    /Safari/i.test(ua) &&
    !/Chrome|CriOS|Edg|OPR|Firefox|FxiOS/i.test(ua)
  );
}

export function currentInstallState(): InstallState {
  const p = platform();
  // Runtime display mode is authoritative. A persisted appinstalled flag
  // cannot prove that the user has not subsequently uninstalled the PWA.
  const installed = isStandalone();

  return {
    installed,
    nativePromptAvailable: deferredPrompt !== null,
    platform: p,
    iosSafari: p === 'ios' && safari(),
    macSafari: p === 'macos' && safari(),
  };
}

function emit(): void {
  const state = currentInstallState();

  listeners.forEach(listener => {
    try { listener(state); } catch { /* listener isolation */ }
  });
}

export function installPromptSnoozed(): boolean {
  const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
  return Number.isFinite(until) && until > Date.now();
}

export function snoozeInstallPrompt(): void {
  localStorage.setItem(
    SNOOZE_KEY,
    String(Date.now() + SNOOZE_MS),
  );
  emit();
}

export function clearInstallSnooze(): void {
  localStorage.removeItem(SNOOZE_KEY);
  emit();
}

export function subscribeInstallState(
  listener: (state: InstallState) => void,
): () => void {
  listeners.add(listener);
  listener(currentInstallState());

  return () => listeners.delete(listener);
}

export async function requestNativeInstall():
Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';

  const event = deferredPrompt;
  deferredPrompt = null;

  await event.prompt();
  const choice = await event.userChoice;

  if (choice.outcome === 'accepted') {
    localStorage.setItem(INSTALLED_KEY, '1');
    localStorage.removeItem(SNOOZE_KEY);
  } else {
    snoozeInstallPrompt();
  }

  emit();
  return choice.outcome;
}

window.addEventListener(
  'beforeinstallprompt',
  event => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  },
);

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  localStorage.setItem(INSTALLED_KEY, '1');
  localStorage.removeItem(SNOOZE_KEY);
  emit();
});

window.matchMedia?.('(display-mode: standalone)')
  .addEventListener?.('change', emit);
